// saffronShimmerDirective.js

const shimmerDataKey = "SaffronShimmer";

// Helpers for storing directive state in el.dataset
function getShimmerState(el) {
  const val = el.dataset[shimmerDataKey];
  if (!val) return {};
  try {
    return JSON.parse(val);
  } catch {
    return {};
  }
}

function setShimmerState(el, state) {
  el.dataset[shimmerDataKey] = JSON.stringify(state);
}

// Helper: Convert a JS style object into CSS text
function styleObjToCss(styleObj) {
  return Object.entries(styleObj)
    .map(([prop, val]) => `${prop}: ${val};`)
    .join("\n");
}

export default {
  mounted(el, binding) {
    let bindingValue = binding.value;
    let inputOptions;

    if (!bindingValue) {
      inputOptions = {};
    }

    if (typeof bindingValue === "string") {
      inputOptions = {
        color: bindingValue,
      };
    }

    if (bindingValue && typeof bindingValue === "object") {
      inputOptions = {
        ...bindingValue,
      };
    }

    let options = {
      // defaults
      color: "var(--c-alt)", // The single color for your shimmer
      trackMouse: true, // true => arc follows the mouse
      angleOffset: 90, // If you want to shift the wedge angle
      fadeMaxDistance: 600, // Distance for fade-out
      initialAngle: -90, // Start angle if not following mouse
      frameDelay: 1, // Interval (ms) for rotation
      degreesPerFrame: 2,
      spread: 30, // Arc degrees on each side
      ...inputOptions,
    };

    const {
      color,
      trackMouse,
      angleOffset,
      fadeMaxDistance,
      initialAngle,
      frameDelay,
      degreesPerFrame,
      spread,
    } = options;

    // ======================================
    // 2) If element is static, make it relative
    // ======================================
    const computedStyle = getComputedStyle(el);
    let changedPosition = false;
    if (computedStyle.position === "static") {
      el.style.position = "relative";
      changedPosition = true;
    }

    // We'll inset by the element's border width if any, else 1px
    const borderWidth =
      computedStyle.borderWidth && computedStyle.borderWidth !== ""
        ? computedStyle.borderWidth
        : "1px";

    // Make a unique class name
    const uniqueClass = "shimmer-" + Math.random().toString(36).slice(2, 8);
    el.classList.add(uniqueClass);

    // ======================================
    // 3) Base & ::after style objects
    // ======================================
    const baseStyles = {
      position: "relative",
      "border-radius": computedStyle.borderRadius,
      overflow: "visible",
    };

    // The ::after shimmer arc
    const afterStyles = {
      content: '""',
      position: "absolute",
      inset: `-${borderWidth}`,
      border: `${borderWidth} solid transparent`,
      "border-radius": "inherit",

      // Single color
      background: color,
      "background-attachment": "fixed",

      // Conic gradient mask = arc
      "-webkit-mask":
        `linear-gradient(#0000,#0000), ` +
        `conic-gradient(from calc((var(--start, ${initialAngle}) - ${spread}) * 1deg), #0000 0deg, #fff, #0000 ${
          spread * 2
        }deg)`,
      mask:
        `linear-gradient(#0000,#0000), ` +
        `conic-gradient(from calc((var(--start, ${initialAngle}) - ${spread}) * 1deg), #0000 0deg, #fff, #0000 ${
          spread * 2
        }deg)`,

      "-webkit-mask-clip": "padding-box,border-box",
      "mask-clip": "padding-box,border-box",
      "-webkit-mask-composite": "source-in,xor",
      "mask-composite": "intersect",

      // We'll fade in/out by setting --active in mouse mode
      opacity: "var(--active, 1)",
      transition: "opacity 0.3s",
    };

    // Inject the CSS
    const cssText = `
.${uniqueClass} {
  ${styleObjToCss(baseStyles)}
}

.${uniqueClass}::after {
  ${styleObjToCss(afterStyles)}
}
`;
    const styleEl = document.createElement("style");
    styleEl.textContent = cssText;
    document.head.appendChild(styleEl);

    // ======================================
    // 4) Animation: Interval or Mouse Tracking
    // ======================================
    let shimmerState = getShimmerState(el);

    let intervalId = null;
    let onMouseMove = null;

    if (!trackMouse) {
      // Simple rotation via setInterval
      let deg = initialAngle;
      intervalId = setInterval(() => {
        deg = (deg + degreesPerFrame) % 360; // rotate from 0..359
        el.style.setProperty("--start", deg.toString());
      }, frameDelay);
    } else {
      // Mouse-based arc movement + fade
      onMouseMove = (e) => {
        // 1) Center of the element
        const rect = el.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        // 2) dx/dy
        const dx = e.clientX - centerX;
        const dy = e.clientY - centerY;

        // 3) Angle + offset
        let angleDeg = Math.atan2(dy, dx) * (180 / Math.PI);
        angleDeg += angleOffset;
        el.style.setProperty("--start", angleDeg.toString());

        // 4) Distance => fade
        const dist = Math.sqrt(dx * dx + dy * dy);
        let distFactor = 1 - dist / fadeMaxDistance;
        if (distFactor < 0) distFactor = 0;
        if (distFactor > 1) distFactor = 1;
        el.style.setProperty("--active", distFactor.toString());
      };
      document.addEventListener("mousemove", onMouseMove);
    }

    // Save references for cleanup
    shimmerState.styleEl = styleEl;
    shimmerState.uniqueClass = uniqueClass;
    shimmerState.intervalId = intervalId;
    shimmerState.onMouseMove = onMouseMove;
    shimmerState.changedPosition = changedPosition;
    setShimmerState(el, shimmerState);
  },

  unmounted(el) {
    // Cleanup on unmount
    const shimmerState = getShimmerState(el);

    // Stop interval if any
    if (shimmerState.intervalId) {
      clearInterval(shimmerState.intervalId);
    }

    // Remove mouse listener if any
    if (shimmerState.onMouseMove) {
      document.removeEventListener("mousemove", shimmerState.onMouseMove);
    }

    // Remove <style> if present
    if (shimmerState?.styleEl?.parentNode) {
      shimmerState.styleEl.parentNode.removeChild(shimmerState.styleEl);
    }

    // Revert position if changed
    if (shimmerState.changedPosition) {
      el.style.position = "";
    }
  },
};
